import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { axiosInstance } from "@/pages/auth/auth.context";
import {
  EventStatusEnum,
  statusLabels,
  statusColors,
  statusBgColors,
} from "@/types/eventStatus";
import { statusIcons } from "@/types/eventStatusIcons";

interface BidStatusUpdateProps {
  eventId: string | undefined;
  currentStatus?: EventStatusEnum;
  onStatusUpdate?: (newStatus: EventStatusEnum) => void;
}

export function BidStatusUpdate({
  eventId,
  currentStatus = EventStatusEnum.NEW,
  onStatusUpdate,
}: BidStatusUpdateProps) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] =
    useState<EventStatusEnum>(currentStatus);

  const handleUpdateStatus = async () => {
    if (!eventId || selectedStatus === currentStatus) {
      setIsOpen(false);
      return;
    }

    setIsUpdating(true);
    try {
      onStatusUpdate(selectedStatus);
    } catch (error) {
      console.error("Error updating bid status:", error);
      toast({
        title: "Error",
        description: "Failed to update bid status",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
      setIsOpen(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="flex gap-2">
          <Badge
            className={`${statusColors[currentStatus]} px-2 py-1 cursor-pointer`}
          >
            <span className="flex items-center gap-1">
              {statusIcons[currentStatus]}
              {statusLabels[currentStatus]}
            </span>
          </Badge>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3">
        <div className="space-y-4">
          <h4 className="font-medium">Update Bid Status</h4>
          <Select
            value={selectedStatus}
            onValueChange={(value) =>
              setSelectedStatus(value as EventStatusEnum)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusLabels).map(([status, label]) => (
                <SelectItem
                  key={status}
                  value={status}
                  className="flex items-center gap-2"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        statusBgColors[status as EventStatusEnum]
                      }`}
                    />
                    {label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="mt-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateStatus}
              disabled={isUpdating || selectedStatus === currentStatus}
              size="sm"
              className="mt-2"
            >
              {isUpdating ? "Updating..." : "Update Status"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
